import graphql__754659161bdc5473eca7 from "./__generated__/InterviewResponseSection_answer.graphql.js";import { useCallback, useId, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import { useFormatMessage } from "../Intl/Context";
import {
  ANSWER_MODE_VOICE,
  ANSWER_MODE_WRITTEN,
  INTERVIEW_STATUS_IN_PROGRESS,
  INTERVIEW_UI_STATE_ASSISTANT_SPEAKING,
  INTERVIEW_UI_STATE_MIC_CHECK,
  INTERVIEW_UI_STATE_READY_TO_SPEAK,
  INTERVIEW_UI_STATE_RECORDING,
  INTERVIEW_UI_STATE_REVIEW,
  INTERVIEW_UI_STATE_THINKING,
  INTERVIEW_UI_STATE_TRANSCRIBING,
  resolveRecorderUiStateTransition
} from "./oralInterviewUiTransitions";
import { oralInterviewMutationFeedback } from "./oralInterviewMutationUtils";
import Button from "../Buttons/Button";
import SpeechToTextRecorder from "../SpeechToText/SpeechToTextRecorder";
import InterviewMicroCheckSection from "./InterviewMicroCheckSection";

import { continueOralInterview } from "../../mutations/ContinueOralInterviewMutation";

const InterviewResponseSection = ({
  resourceId,
  initialAnswerMode,
  initialMicCheckPassed,
  interviewUiState,
  onInterviewUiStateChange,
  isBusy,
  onBusyStateChange,
  playAssistantAudio,
  ...props
}) => {
  const { status, formField } = useFragment(
    InterviewResponseSection_answer,
    props.answer
  );
  const t = useFormatMessage();

  const transcriptionInputId = useId();
  const transcriptionLabelId = `${transcriptionInputId}-label`;

  const transcriptionLabel = t(intlMessages.transcriptionLabel);
  const retryLabel = t(intlMessages.retryTranscription);
  const validateLabel = t(intlMessages.validateResponse);
  const writeAnswerLabel = t(intlMessages.writeAnswer);
  const switchToVoiceLabel = t(intlMessages.switchToVoice);
  const waitForAssistantMessage = t(intlMessages.recorderWaitAssistant);
  const genericErrorMessage = t(intlMessages.genericError);
  const networkErrorMessage = t(intlMessages.networkError);

  const [transcription, setTranscription] = useState("");
  const [answerMode, setAnswerMode] = useState(initialAnswerMode);
  const [hasPassedMicCheck, setHasPassedMicCheck] = useState(initialMicCheckPassed);
  const [feedbackMessage, setFeedbackMessage] = useState(null);

  const isVoiceMode = answerMode === ANSWER_MODE_VOICE;
  const isMicCheckOpen = interviewUiState === INTERVIEW_UI_STATE_MIC_CHECK;
  const showTranscriptionReview = interviewUiState === INTERVIEW_UI_STATE_REVIEW;
  const isInterviewInProgress = status === INTERVIEW_STATUS_IN_PROGRESS;
  const shouldDisplayResponse =
    isInterviewInProgress &&
    interviewUiState !== INTERVIEW_UI_STATE_THINKING &&
    !isMicCheckOpen;
  const shouldShowRecorder =
    isVoiceMode &&
    !showTranscriptionReview &&
    interviewUiState !== INTERVIEW_UI_STATE_ASSISTANT_SPEAKING;
  const canWriteAnswer =
    shouldShowRecorder &&
    interviewUiState !== INTERVIEW_UI_STATE_RECORDING &&
    interviewUiState !== INTERVIEW_UI_STATE_TRANSCRIBING;
  const showAnswerTextarea = isVoiceMode ? showTranscriptionReview : true;

  const reviewActionsClassName = classNames("oral-interview-actions", "margin-top-xs", {
    "oral-interview-actions--split": isVoiceMode
  });

  const handleContinueCompleted = useCallback(
    response => {
      onBusyStateChange(false);

      const result = response?.continueOralInterview;
      const mutationFeedbackMessage = oralInterviewMutationFeedback(
        result,
        genericErrorMessage
      );

      setFeedbackMessage(mutationFeedbackMessage);
      if (mutationFeedbackMessage) {
        onInterviewUiStateChange(INTERVIEW_UI_STATE_REVIEW);
        return;
      }

      setTranscription("");
      playAssistantAudio(result);
    },
    [genericErrorMessage, onBusyStateChange, onInterviewUiStateChange, playAssistantAudio]
  );

  const handleContinueFailed = useCallback(() => {
    onBusyStateChange(false);
    setFeedbackMessage(networkErrorMessage);
    onInterviewUiStateChange(INTERVIEW_UI_STATE_REVIEW);
  }, [networkErrorMessage, onBusyStateChange, onInterviewUiStateChange]);

  const handleTranscriptionValidated = useCallback(() => {
    setFeedbackMessage(null);
    onBusyStateChange(true);
    onInterviewUiStateChange(INTERVIEW_UI_STATE_THINKING);
    continueOralInterview(
      {
        resourceId,
        formFieldId: formField.id,
        userMessage: transcription.trim()
      },
      handleContinueCompleted,
      handleContinueFailed
    );
  }, [
    formField.id,
    handleContinueCompleted,
    handleContinueFailed,
    onBusyStateChange,
    onInterviewUiStateChange,
    resourceId,
    transcription
  ]);

  const handleTranscriptionChanged = useCallback(event => {
    setTranscription(event.target.value);
  }, []);

  const handleTranscriptionRetried = useCallback(() => {
    setFeedbackMessage(null);
    setTranscription("");
    onInterviewUiStateChange(INTERVIEW_UI_STATE_READY_TO_SPEAK);
  }, [onInterviewUiStateChange]);

  const handleWritingModeSelected = useCallback(() => {
    setFeedbackMessage(null);
    setTranscription("");
    setAnswerMode(ANSWER_MODE_WRITTEN);
  }, []);

  const handleVoiceModeSelected = useCallback(() => {
    if (hasPassedMicCheck) {
      setAnswerMode(ANSWER_MODE_VOICE);
      onInterviewUiStateChange(INTERVIEW_UI_STATE_READY_TO_SPEAK);
      return;
    }

    onInterviewUiStateChange(INTERVIEW_UI_STATE_MIC_CHECK);
  }, [hasPassedMicCheck, onInterviewUiStateChange]);

  const handleMicCheckPassed = useCallback(() => {
    setHasPassedMicCheck(true);
    setAnswerMode(ANSWER_MODE_VOICE);
    onInterviewUiStateChange(INTERVIEW_UI_STATE_READY_TO_SPEAK);
  }, [onInterviewUiStateChange]);

  const handleMicCheckCancelled = useCallback(() => {
    onInterviewUiStateChange(INTERVIEW_UI_STATE_READY_TO_SPEAK);
  }, [onInterviewUiStateChange]);

  const handleRecorderResult = useCallback(
    nextTranscription => {
      setFeedbackMessage(null);
      setTranscription(nextTranscription);
      onInterviewUiStateChange(INTERVIEW_UI_STATE_REVIEW);
    },
    [onInterviewUiStateChange]
  );

  const handleRecorderStateChanged = useCallback(
    recorderState => {
      const nextUiState = resolveRecorderUiStateTransition({
        currentUiState: interviewUiState,
        recorderState,
        transcription
      });
      if (!nextUiState || nextUiState === interviewUiState) return;

      onInterviewUiStateChange(nextUiState);
    },
    [interviewUiState, onInterviewUiStateChange, transcription]
  );

  return (
    <>
      {isMicCheckOpen && (
        <InterviewMicroCheckSection
          resourceId={resourceId}
          onMicCheckPassed={handleMicCheckPassed}
          onContinueInWriting={handleMicCheckCancelled}
          isBusy={isBusy}
        />
      )}

      {shouldDisplayResponse && (
        <div className="oral-interview-recorder">
          {interviewUiState === INTERVIEW_UI_STATE_ASSISTANT_SPEAKING && (
            <p className="form-text margin-bottom-xs">{waitForAssistantMessage}</p>
          )}

          {(shouldShowRecorder || showAnswerTextarea) && (
            <div className="oral-interview-response-header margin-bottom-xs">
              {showAnswerTextarea ? (
                <label
                  id={transcriptionLabelId}
                  htmlFor={transcriptionInputId}
                  className="text-legend margin-none"
                >
                  {transcriptionLabel}
                </label>
              ) : (
                <p id={transcriptionLabelId} className="text-legend margin-none">
                  {transcriptionLabel}
                </p>
              )}

              {canWriteAnswer && (
                <Button
                  type="button"
                  className="btn btn-primary-inverse btn-shadow"
                  onClick={handleWritingModeSelected}
                  disabled={isBusy}
                  label={writeAnswerLabel}
                  iconClassName="fa fa-keyboard"
                />
              )}

              {!isVoiceMode && (
                <Button
                  type="button"
                  className="btn btn-primary-inverse btn-shadow"
                  onClick={handleVoiceModeSelected}
                  disabled={isBusy}
                  label={switchToVoiceLabel}
                  iconClassName="fa fa-microphone"
                />
              )}
            </div>
          )}

          {shouldShowRecorder && (
            <SpeechToTextRecorder
              resourceId={resourceId}
              onResult={handleRecorderResult}
              onCancel={handleTranscriptionRetried}
              onStateChange={handleRecorderStateChanged}
              disabled={isBusy}
            />
          )}

          {showAnswerTextarea && (
            <div className="oral-interview-transcription-review">
              <textarea
                id={transcriptionInputId}
                aria-labelledby={transcriptionLabelId}
                className="form-control oral-interview-transcription-edit"
                value={transcription}
                onChange={handleTranscriptionChanged}
              />

              <div className={reviewActionsClassName}>
                {isVoiceMode && (
                  <Button
                    type="button"
                    className="btn btn-primary-inverse"
                    onClick={handleTranscriptionRetried}
                    disabled={isBusy}
                    label={retryLabel}
                  />
                )}

                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleTranscriptionValidated}
                  disabled={!transcription.trim() || isBusy}
                  label={validateLabel}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {feedbackMessage && (
        <p className="form-text text-danger margin-top-xs">{feedbackMessage}</p>
      )}
    </>
  );
};

const InterviewResponseSection_answer = graphql__754659161bdc5473eca7;

export default InterviewResponseSection;

const intlMessages = defineMessages({
  genericError: {
    id: "answer.oralInterview.genericError",
    defaultMessage: "L'entretien vocal n'a pas pu être mis à jour."
  },
  networkError: {
    id: "answer.oralInterview.networkError",
    defaultMessage: "Une erreur réseau est survenue. Merci de réessayer."
  },
  recorderWaitAssistant: {
    id: "answer.oralInterview.recorderWaitAssistant",
    defaultMessage: "Saisissez votre réponse après la fin de l'audio."
  },
  retryTranscription: {
    id: "answer.oralInterview.retryTranscription",
    defaultMessage: "Réessayer"
  },
  switchToVoice: {
    id: "answer.oralInterview.switchToVoice",
    defaultMessage: "Répondre à l'oral"
  },
  transcriptionLabel: {
    id: "answer.oralInterview.transcriptionLabel",
    defaultMessage: "Votre réponse :"
  },
  validateResponse: {
    id: "answer.oralInterview.validateResponse",
    defaultMessage: "Envoyer ma réponse"
  },
  writeAnswer: {
    id: "answer.oralInterview.writeAnswer",
    defaultMessage: "Répondre par écrit"
  }
});
